import { Logger } from '../logging';
import type { TrackingInterface } from './_types';

interface TrackingHandlerOptions {
  _resolvedWhitelist?: string[];
  _excludedTrackers?: string[];
}

interface TrackingHandlerInterface extends TrackingInterface {
  hasTracker: (trackerId: string) => boolean;
  getTracker: (trackerId: string) => TrackingInterface | undefined;
  addTracker: (trackerId: string, logger: TrackingInterface) => void;
  setTags: NonNullable<TrackingInterface['setTags']>;
  trackWithWhitelist: (
    message: string,
    context?: Parameters<NonNullable<TrackingInterface['track']>>[1],
    options?: Parameters<NonNullable<TrackingInterface['track']>>[2],
    handlerOptions?: TrackingHandlerOptions,
  ) => void;
  flushTrackers: (trackerIds: string[]) => Promise<void>;
  initialize: () => Promise<void>;
}

function TrackingHandler(): TrackingHandlerInterface {
  const trackers: Map<string, TrackingInterface> = new Map();

  // SQR-13012: async trackers have no client until initialize() resolves —
  // Rudderstack, for instance, pulls its SDK in via a dynamic import() — so an
  // event fired during app boot (notably the first screen-name page view from
  // the root layout) would otherwise be dropped on the floor. Hold every
  // pre-init dispatch here and replay it, in order, once all trackers have
  // initialized; after that, dispatch is immediate. Bounded so a never-resolved
  // initialize() can't leak memory unboundedly.
  const MAX_PENDING_DISPATCHES = 100;
  const pendingDispatches: Array<() => void> = [];
  let isInitialized = false;

  const dispatch = (deliver: () => void) => {
    if (isInitialized) {
      deliver();
      return;
    }
    // Drop the oldest rather than the newest: if we ever hit the cap, the most
    // recent events are the ones most likely to still matter.
    if (pendingDispatches.length >= MAX_PENDING_DISPATCHES) {
      pendingDispatches.shift();
    }
    pendingDispatches.push(deliver);
  };

  // SQR-13012: one tracker throwing must never abort fan-out to the others or
  // propagate into UI callers (see the Clarity consent-gate incident). Each
  // per-tracker call is isolated so the whitelist/excluded-tracker contract
  // (and its tests) is preserved.
  const safeCall = (trackerId: string, fn?: () => void) => {
    try {
      fn?.();
    } catch (error) {
      Logger.error(`[TRACKING] Tracker "${trackerId}" threw`, { error });
    }
  };

  const initialize = async () => {
    await Promise.allSettled(
      Array.from(trackers.entries()).map(async ([trackerId, tracker]) => {
        try {
          await tracker.initialize?.();
        } catch (error) {
          Logger.error(
            `[TRACKING] Failed to initialize tracker: ${trackerId}`,
            { error },
          );
        }
      }),
    );

    isInitialized = true;
    const flushed = pendingDispatches.splice(0);
    flushed.forEach((deliver) => deliver());

    Logger.console('[TRACKING] Initialized successfully', {
      trackers: Array.from(trackers.keys()),
      flushed: flushed.length,
    });
  };

  const track: TrackingHandlerInterface['track'] = (
    message,
    context,
    options,
  ) => {
    dispatch(() => {
      trackers.forEach((tracker, trackerId) => {
        safeCall(trackerId, () => tracker.track?.(message, context, options));
      });
    });
  };

  const trackWithWhitelist: TrackingHandlerInterface['trackWithWhitelist'] = (
    message,
    context,
    options,
    handlerOptions,
  ) => {
    const resolvedWhitelist = handlerOptions?._resolvedWhitelist;
    const excludedTrackers = handlerOptions?._excludedTrackers;

    dispatch(() => {
      trackers.forEach((tracker, trackerId) => {
        if (resolvedWhitelist && !resolvedWhitelist.includes(trackerId)) {
          return;
        }
        if (excludedTrackers?.includes(trackerId)) {
          return;
        }
        safeCall(trackerId, () => tracker.track?.(message, context, options));
      });
    });
  };

  const trackScreenName: TrackingHandlerInterface['trackScreenName'] = (
    name,
    params,
  ) => {
    dispatch(() => {
      trackers.forEach((tracker, trackerId) => {
        safeCall(trackerId, () => tracker.trackScreenName?.(name, params));
      });
    });
  };

  const setUserId: TrackingHandlerInterface['setUserId'] = (
    userId,
    context,
  ) => {
    dispatch(() => {
      trackers.forEach((tracker, trackerId) => {
        safeCall(trackerId, () => tracker.setUserId?.(userId, context));
      });
    });
  };

  const setTags: TrackingHandlerInterface['setTags'] = (tags) => {
    dispatch(() => {
      trackers.forEach((tracker, trackerId) => {
        safeCall(trackerId, () => tracker.setTags?.(tags));
      });
    });
  };

  const flushTrackers: TrackingHandlerInterface['flushTrackers'] = async (
    trackerIds,
  ) => {
    await Promise.allSettled(
      trackerIds.map((id) => trackers.get(id)?.flush?.()),
    );
  };

  const addTracker = (trackerId: string, tracker: TrackingInterface) => {
    trackers.set(trackerId, tracker);
  };

  const hasTracker = (trackerId: string) => {
    return trackers.has(trackerId);
  };

  const getTracker = (trackerId: string) => {
    return trackers.get(trackerId);
  };

  return Object.freeze({
    initialize,
    track,
    trackWithWhitelist,
    flushTrackers,
    trackScreenName,
    setUserId,
    setTags,
    addTracker,
    hasTracker,
    getTracker,
  });
}

export { TrackingHandler };
